import { useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useRBAC } from './RBACContext';
import type { IndustryType } from '@/types';
import {
  SettingsFilterContext,
  type SettingsFilterCompany,
  type SettingsFilterGroup,
  type SettingsFilterContextType,
} from './SettingsFilterContext';

// Storage key prefix - user-specific to prevent cross-user data leakage
const STORAGE_PREFIX = 'settings_filters_';

function getStorageKey(userId: string, key: string): string {
  return `${STORAGE_PREFIX}${userId}_${key}`;
}

function loadFromStorage<T>(userId: string, key: string, defaultValue: T): T {
  if (!userId) return defaultValue;
  try {
    const stored = localStorage.getItem(getStorageKey(userId, key));
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function saveToStorage<T>(userId: string, key: string, value: T): void {
  if (!userId) return;
  try {
    localStorage.setItem(getStorageKey(userId, key), JSON.stringify(value));
  } catch (error) {
    console.warn('[SettingsFilterContext] Failed to save to localStorage:', error);
  }
}

export function SettingsFilterProvider({ children }: { children: ReactNode }) {
  const { user, companies: rbacCompanies, memberships, loading: rbacLoading, isSuperadmin } = useRBAC();
  const userId = user?.id || '';

  // Selection state - initialized from localStorage or defaults
  const [selectedIndustries, setSelectedIndustriesState] = useState<IndustryType[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIdsState] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIdsState] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Map RBAC companies to our filter company interface
  const companies: SettingsFilterCompany[] = useMemo(() => {
    return rbacCompanies.map(a => ({
      id: a.id,
      name: a.name,
      slug: a.slug,
      industry: a.industry,
      is_internal: a.is_internal,
    }));
  }, [rbacCompanies]);

  // Extract unique groups from memberships
  const groups: SettingsFilterGroup[] = useMemo(() => {
    const groupMap = new Map<string, SettingsFilterGroup>();
    memberships.forEach(m => {
      if (m.group_id && !groupMap.has(m.group_id)) {
        groupMap.set(m.group_id, {
          id: m.group_id,
          name: m.group_name || 'Unknown Group',
          company_id: m.company_id,
          company_name: m.company_name,
        });
      }
    });
    return Array.from(groupMap.values());
  }, [memberships]);

  // Filter companies by selected industries
  const filteredCompanies = useMemo(() => {
    if (selectedIndustries.length === 0) {
      return companies;
    }
    return companies.filter(a => {
      const industry = a.industry || 'film'; // Default to 'film' if no industry set
      return selectedIndustries.includes(industry as IndustryType);
    });
  }, [companies, selectedIndustries]);

  // Filter groups by selected companies
  const filteredGroups = useMemo(() => {
    if (selectedCompanyIds.length === 0) {
      return groups;
    }
    return groups.filter(g => selectedCompanyIds.includes(g.company_id));
  }, [groups, selectedCompanyIds]);

  // Track if superadmin override has been applied (separate from general initialization)
  const [superadminOverrideApplied, setSuperadminOverrideApplied] = useState(false);

  // Stable company/group count to trigger override only when data first becomes available
  const companiesCount = companies.length;
  const groupsCount = groups.length;

  // Superadmin override - runs once when isSuperadmin becomes true and data is available.
  // Dependencies use primitive counts instead of object arrays to prevent re-triggers
  // from referential changes on every RBAC re-render.
  useEffect(() => {
    if (!isSuperadmin || rbacLoading || !userId) return;
    if (superadminOverrideApplied) return;
    if (companiesCount === 0) return;

    const allIndustries = Array.from(new Set<IndustryType>(
      companies.map(a => (a.industry || 'film') as IndustryType)
    ));
    const allCompanyIds = companies.map(a => a.id);
    const allGroupIds = groups.map(g => g.id);

    setSelectedIndustriesState(allIndustries);
    setSelectedCompanyIdsState(allCompanyIds);
    setSelectedGroupIdsState(allGroupIds);

    saveToStorage(userId, 'industries', allIndustries);
    saveToStorage(userId, 'companyIds', allCompanyIds);
    saveToStorage(userId, 'groupIds', allGroupIds);

    setSuperadminOverrideApplied(true);
    setInitialized(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, rbacLoading, userId, superadminOverrideApplied, companiesCount, groupsCount]);

  // Initialize from localStorage for non-superadmins
  useEffect(() => {
    // Skip if superadmin (handled by separate effect above)
    if (isSuperadmin) return;
    if (rbacLoading || !userId || initialized) return;

    // Get available industries from companies
    const availableIndustries = new Set<IndustryType>(
      companies.map(a => (a.industry || 'film') as IndustryType)
    );

    // Non-superadmins: Load saved selections from localStorage
    const savedIndustries = loadFromStorage<IndustryType[]>(userId, 'industries', []);
    const savedCompanyIds = loadFromStorage<string[]>(userId, 'companyIds', []);
    const savedGroupIds = loadFromStorage<string[]>(userId, 'groupIds', []);

    // If no saved industries or all saved are invalid, select all available
    const validSavedIndustries = savedIndustries.filter(i => availableIndustries.has(i));
    if (validSavedIndustries.length === 0) {
      setSelectedIndustriesState(Array.from(availableIndustries));
    } else {
      setSelectedIndustriesState(validSavedIndustries);
    }

    // Validate saved company IDs
    const companyIdSet = new Set(companies.map(a => a.id));
    const validCompanyIds = savedCompanyIds.filter(id => companyIdSet.has(id));
    if (validCompanyIds.length === 0 && companies.length > 0) {
      // Select all companies by default
      setSelectedCompanyIdsState(companies.map(a => a.id));
    } else {
      setSelectedCompanyIdsState(validCompanyIds);
    }

    // Validate saved group IDs
    const groupIdSet = new Set(groups.map(g => g.id));
    const validGroupIds = savedGroupIds.filter(id => groupIdSet.has(id));
    if (validGroupIds.length === 0 && groups.length > 0) {
      // Select all groups by default
      setSelectedGroupIdsState(groups.map(g => g.id));
    } else {
      setSelectedGroupIdsState(validGroupIds);
    }

    setInitialized(true);
  }, [rbacLoading, userId, companies, groups, initialized, isSuperadmin]);

  // Setters with persistence
  const setSelectedIndustries = useCallback((industries: IndustryType[]) => {
    setSelectedIndustriesState(industries);
    saveToStorage(userId, 'industries', industries);

    // When industries change, filter companies and auto-update company selection
    const matchingCompanies = companies.filter(a => {
      const industry = a.industry || 'film';
      return industries.includes(industry as IndustryType);
    });
    const matchingCompanyIds = matchingCompanies.map(a => a.id);
    setSelectedCompanyIdsState(matchingCompanyIds);
    saveToStorage(userId, 'companyIds', matchingCompanyIds);

    // Also update groups to match filtered companies
    const matchingGroups = groups.filter(g => matchingCompanyIds.includes(g.company_id));
    const matchingGroupIds = matchingGroups.map(g => g.id);
    setSelectedGroupIdsState(matchingGroupIds);
    saveToStorage(userId, 'groupIds', matchingGroupIds);
  }, [userId, companies, groups]);

  const setSelectedCompanyIds = useCallback((ids: string[]) => {
    setSelectedCompanyIdsState(ids);
    saveToStorage(userId, 'companyIds', ids);

    // When companies change, filter groups
    const matchingGroups = groups.filter(g => ids.includes(g.company_id));
    const matchingGroupIds = matchingGroups.map(g => g.id);
    setSelectedGroupIdsState(matchingGroupIds);
    saveToStorage(userId, 'groupIds', matchingGroupIds);
  }, [userId, groups]);

  const setSelectedGroupIds = useCallback((ids: string[]) => {
    setSelectedGroupIdsState(ids);
    saveToStorage(userId, 'groupIds', ids);
  }, [userId]);

  const clearAllFilters = useCallback(() => {
    // Get all available industries from companies
    const allIndustries = Array.from(new Set(
      companies.map(a => (a.industry || 'film') as IndustryType)
    ));
    setSelectedIndustriesState(allIndustries);
    saveToStorage(userId, 'industries', allIndustries);

    const allCompanyIds = companies.map(a => a.id);
    setSelectedCompanyIdsState(allCompanyIds);
    saveToStorage(userId, 'companyIds', allCompanyIds);

    const allGroupIds = groups.map(g => g.id);
    setSelectedGroupIdsState(allGroupIds);
    saveToStorage(userId, 'groupIds', allGroupIds);
  }, [userId, companies, groups]);

  // Ensure a specific industry is included in the filter (used after creating items)
  const ensureIndustrySelected = useCallback((industry: IndustryType) => {
    if (!selectedIndustries.includes(industry)) {
      const newIndustries = [...selectedIndustries, industry];
      setSelectedIndustriesState(newIndustries);
      saveToStorage(userId, 'industries', newIndustries);

      // Also update companies and groups to include the new industry
      const matchingCompanies = companies.filter(a => {
        const ind = a.industry || 'film';
        return newIndustries.includes(ind as IndustryType);
      });
      const matchingCompanyIds = matchingCompanies.map(a => a.id);
      setSelectedCompanyIdsState(matchingCompanyIds);
      saveToStorage(userId, 'companyIds', matchingCompanyIds);

      const matchingGroups = groups.filter(g => matchingCompanyIds.includes(g.company_id));
      const matchingGroupIds = matchingGroups.map(g => g.id);
      setSelectedGroupIdsState(matchingGroupIds);
      saveToStorage(userId, 'groupIds', matchingGroupIds);

      // Industry added to filter selection
    }
  }, [selectedIndustries, userId, companies, groups]);

  // Ensure a specific company is included in the filter (used after creating items)
  const ensureCompanySelected = useCallback((companyId: string) => {
    if (!selectedCompanyIds.includes(companyId)) {
      // Find the company to get its industry
      const company = companies.find(c => c.id === companyId);
      if (company) {
        const industry = (company.industry || 'film') as IndustryType;

        // First ensure the industry is selected
        if (!selectedIndustries.includes(industry)) {
          ensureIndustrySelected(industry);
        } else {
          // Industry already selected, just add the company
          const newCompanyIds = [...selectedCompanyIds, companyId];
          setSelectedCompanyIdsState(newCompanyIds);
          saveToStorage(userId, 'companyIds', newCompanyIds);
          // Company added to filter selection
        }
      }
    }
  }, [selectedCompanyIds, selectedIndustries, userId, companies, ensureIndustrySelected]);

  // Check if any filters are actively restricting data
  const hasActiveFilters = useMemo(() => {
    const allIndustries = new Set(companies.map(a => (a.industry || 'film') as IndustryType));
    const industriesFiltered = selectedIndustries.length < allIndustries.size;
    const companiesFiltered = selectedCompanyIds.length < companies.length;
    const groupsFiltered = selectedGroupIds.length < groups.length;

    return industriesFiltered || companiesFiltered || groupsFiltered;
  }, [selectedIndustries, selectedCompanyIds, selectedGroupIds, companies, groups]);

  const contextValue = useMemo<SettingsFilterContextType>(() => ({
    selectedIndustries,
    selectedCompanyIds,
    selectedGroupIds,
    companies,
    groups,
    filteredCompanies,
    filteredGroups,
    setSelectedIndustries,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    clearAllFilters,
    isLoading: rbacLoading || !initialized,
    hasActiveFilters,
    ensureIndustrySelected,
    ensureCompanySelected,
  }), [
    selectedIndustries,
    selectedCompanyIds,
    selectedGroupIds,
    companies,
    groups,
    filteredCompanies,
    filteredGroups,
    setSelectedIndustries,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    clearAllFilters,
    rbacLoading,
    initialized,
    hasActiveFilters,
    ensureIndustrySelected,
    ensureCompanySelected,
  ]);

  return (
    <SettingsFilterContext.Provider value={contextValue}>
      {children}
    </SettingsFilterContext.Provider>
  );
}
